import { useState } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useCloseOwnTicket, extractErrorMessage } from '@/queries/ticketMutations'

/**
 * Lets the person who raised a ticket retire it themselves rather than waiting
 * for staff. Deliberately quiet and secondary — closing is a fine thing to do
 * but it is never the action we're asking the customer for. The confirm step is
 * inline (two clicks, no modal) because the whole point is that this stays out
 * of the way.
 *
 * Only the reporter is offered it; the server enforces the same rule
 * independently (supportTicket.service#closeOwnTicket).
 */
export function CloseTicketButton({ ticketId }: { ticketId: string }) {
  const close = useCloseOwnTicket(ticketId)
  const [confirming, setConfirming] = useState(false)

  if (!confirming) {
    return (
      <Button variant="ghost" size="sm" className="text-etn-text-label" onClick={() => setConfirming(true)}>
        <CheckCircle2 />
        Close ticket
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-2 text-etn-control text-etn-text-label">
      <span>Close this ticket?</span>
      <Button
        variant="secondary"
        size="sm"
        disabled={close.isPending}
        onClick={async () => {
          try {
            await close.mutateAsync()
            toast.success('Ticket closed')
          } catch (err) {
            toast.error(extractErrorMessage(err, 'Could not close the ticket'))
            setConfirming(false)
          }
        }}
      >
        {close.isPending ? <Loader2 className="animate-spin" /> : null}
        Yes, close it
      </Button>
      <Button variant="ghost" size="sm" disabled={close.isPending} onClick={() => setConfirming(false)}>
        Cancel
      </Button>
    </div>
  )
}
